import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/adminApi";
import { fmtDate, fmtMoney, statusBadge } from "@/pages/admin/util";
import { Loader2, RefreshCw } from "lucide-react";

const STATUSES = ["", "PAID", "PENDING", "FAILED"];
const TYPES = ["", "MEMBERSHIP", "INVOICE", "CUSTOM_PAYMENT"];

export default function Payments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (type) params.payment_type = type;
      const { data } = await adminApi.get("/payments", { params });
      setItems(data.items);
    } finally { setLoading(false); }
  }, [status, type]);

  useEffect(() => { load(); }, [load]);

  const total = items.filter(i => i.status === "PAID").reduce((acc, i) => acc + Number(i.amount || 0), 0);

  return (
    <div data-testid="admin-payments-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="eyebrow">Cash control</span>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Payments</h1>
          <p className="text-sm text-[#475569] mt-1">Total paid in view: <span className="text-[#071A33] font-medium">{fmtMoney(total)}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <select className="field-input h-10" value={type} onChange={(e) => setType(e.target.value)} data-testid="pay-type-filter">
            {TYPES.map((t) => <option key={t} value={t}>{t || "All types"}</option>)}
          </select>
          <select className="field-input h-10" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pay-status-filter">
            {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
          <button onClick={load} className="btn-outline h-10 py-0 px-4"><RefreshCw size={14}/></button>
        </div>
      </div>

      <div className="mt-6 admin-panel overflow-x-auto">
        <table className="w-full text-sm" data-testid="payments-table">
          <thead>
            <tr className="bg-[#F7F4ED] text-left text-[#475569] text-xs uppercase tracking-[0.15em]">
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Stripe IDs</th>
              <th className="px-4 py-3">Paid</th>
              <th className="px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={16}/></td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#475569]">No payments yet.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-[#E2DCD0]">
                <td className="px-4 py-3 text-[#475569]">{p.payment_type}</td>
                <td className="px-4 py-3 font-medium text-[#071A33]">{fmtMoney(p.amount)}</td>
                <td className="px-4 py-3 text-[#475569]">{p.customer_email || "—"}</td>
                <td className="px-4 py-3 text-[#475569]">{p.reference || p.invoice_number || "—"}</td>
                <td className="px-4 py-3">{statusBadge(p.status)}</td>
                <td className="px-4 py-3 text-[#475569] font-mono text-[11px] max-w-[200px] truncate">
                  {p.stripe_session_id && <div className="truncate">{p.stripe_session_id}</div>}
                  {p.stripe_payment_intent_id && <div className="truncate">{p.stripe_payment_intent_id}</div>}
                  {!p.stripe_session_id && !p.stripe_payment_intent_id && "—"}
                </td>
                <td className="px-4 py-3 text-[#475569]">{fmtDate(p.paid_at, true)}</td>
                <td className="px-4 py-3 text-[#475569]">{fmtDate(p.created_at, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
